import React, { useEffect, useMemo, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import ModalsLoader from "../Modals/ModalsLoader";
import clsx from "clsx";
import useAllTransactions from "src/hooks/useAllTransactions";
import useCurrentUser from "src/hooks/useCurrentUser";
import useCurrentAccount from "src/hooks/useCurrentAccount";
import useFilteredTransactions from "src/hooks/useFilteredTransactions";
import toast from "react-hot-toast";
import useMobile from "src/hooks/useMobile";
import useTheme from "src/hooks/useTheme";
import { useTranslation } from "react-i18next";
import LoadingWithText from "../LoadingWithText/LoadingWithText";
import { ROUTES } from "src/helpers/routes";
import LoadingWithTextPage from "../LoadingWithText/LoadingWithTextPage";
import useMainFilter from "src/hooks/useMainFilter";
import useFrequentTransactions from "src/hooks/useFrequentTransactions";
import { APP_TOKEN_KEY } from "src/constants/app";
import { ICON_SIZES } from "src/constants/icons";

const ProtectedRoutes: React.FC = () => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  const location = useLocation();
  const { t } = useTranslation(["misc"]);
  const { updateThemeMetaTags } = useTheme();

  const {
    isLoadingAllTransactions,
    isLoadingCustomizedCategories,
    isLoadingCustomizedSources,
    isRefetchingAllTransactions,
    isRefetchingCustomizedCategories,
    isRefetchingCustomizedSources,
  } = useAllTransactions();

  const { isRefetchingFilteredTransactions } = useFilteredTransactions();
  const { isRefetchingFrequentTransactions, isLoadingFrequentTransactions } =
    useFrequentTransactions();
  const { isLoadingAccounts } = useCurrentUser();
  const { isLoadingCurrentAccount } = useCurrentAccount();
  const { mainFilter } = useMainFilter();
  const { isMobile } = useMobile();

  const toastIdRef = useRef<string | undefined>(undefined);

  const isLoadingSomething = useMemo(() => {
    return [
      !!isLoadingAccounts,
      !!isLoadingAllTransactions,
      !!isLoadingCustomizedCategories,
      !!isLoadingCustomizedSources,
      !!isLoadingFrequentTransactions,
      !!(mainFilter?.account ? isLoadingCurrentAccount : false),
    ].includes(true);
  }, [
    isLoadingAccounts,
    isLoadingAllTransactions,
    isLoadingCustomizedCategories,
    isLoadingCustomizedSources,
    mainFilter?.account,
    isLoadingCurrentAccount,
    isLoadingFrequentTransactions,
  ]);

  const isRefetchingSomething = useMemo(() => {
    return [
      !!isRefetchingAllTransactions,
      !!isRefetchingCustomizedCategories,
      !!isRefetchingCustomizedSources,
      !!isRefetchingFilteredTransactions,
      !!isRefetchingFrequentTransactions,
    ].includes(true);
  }, [
    isRefetchingAllTransactions,
    isRefetchingCustomizedCategories,
    isRefetchingCustomizedSources,
    isRefetchingFilteredTransactions,
    isRefetchingFrequentTransactions,
  ]);

  useEffect(() => {
    if (isRefetchingSomething && !toastIdRef.current) {
      toastIdRef.current = toast.loading(t("misc:strings.syncing"), {
        position: isMobile ? "bottom-center" : "bottom-right",
        duration: 3000,
        icon: <LoadingWithText size={ICON_SIZES.SM} />,
      });
    }

    if (!isRefetchingSomething && toastIdRef.current) {
      const currentToastId = toastIdRef.current;

      const timeout = setTimeout(() => {
        toast.dismiss(currentToastId);
        toastIdRef.current = undefined;
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [isRefetchingSomething, isMobile, t]);

  useEffect(() => {
    updateThemeMetaTags();
  }, [updateThemeMetaTags]);

  if (!token) {
    return <Navigate to={ROUTES.ROOT} state={{ from: location }} replace />;
  }

  if (isLoadingSomething) {
    return (
      <div className={clsx(["flex", "h-full", "w-full", "fixed", "inset-0"])}>
        <LoadingWithTextPage />
      </div>
    );
  }

  return (
    <>
      <ModalsLoader />
      <Outlet />
    </>
  );
};

export default ProtectedRoutes;
